import React from 'react';

import { classnames, margin } from '@lumapps/classnames';
import { Breadcrumb, type BreadcrumbProps } from '@lumapps/lumx-lists/components/Breadcrumb';
import { Divider, Orientation, Size } from '@lumapps/lumx/react';
import { DocumentV1 } from '@lumapps/medias-document/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { NavigationPathItem } from '@lumapps/utils/hooks/useNavigation';

export interface FilePickerDialogBreadcrumbProps {
    /** The current path of folders to display in the breadcrumb. */
    folderPath: NavigationPathItem<DocumentV1>[];
    /** Callback function triggered when a breadcrumb item is clicked, used to navigate to that level. */
    navigateBreadcrumb: (index: number) => void;
}

/**
 * Renders a breadcrumb navigation for a file picker.
 * This component displays the current folder hierarchy and allows users
 * to navigate back to parent folders.
 *
 * @param {FilePickerDialogBreadcrumbProps}
 * @returns FilePickerDialogBreadcrumb
 */
export const FilePickerDialogBreadcrumb: React.FC<FilePickerDialogBreadcrumbProps> = ({
    folderPath,
    navigateBreadcrumb,
}) => {
    const { translateKey, translate } = useTranslate();

    const breadcrumbProps: BreadcrumbProps | undefined = React.useMemo(() => {
        if (folderPath.length > 1) {
            return {
                levels: folderPath.map((folder, index) => ({
                    label: translate(folder.name) || undefined,
                    ariaLabel: translate(folder.name) || translateKey(GLOBAL.HOME),
                    onClick: () => navigateBreadcrumb(index),
                })),
            };
        }

        return undefined;
    }, [folderPath, navigateBreadcrumb, translate, translateKey]);

    if (!breadcrumbProps) {
        return null;
    }

    return (
        <>
            <Divider />

            <Breadcrumb
                className={classnames(margin(Orientation.vertical, Size.big))}
                paddingHorizontal={Size.big}
                {...breadcrumbProps}
            />
        </>
    );
};
